// Ctrl+K's index: every record a person might jump to, by its codes (SKU, barcode, vendor SKU, PO, run, bill and
// receipt numbers, vendor refs) and its words. Built when the palette opens; a keystroke is one Map lookup for an exact
// code plus one pass of plain substring tests, well under 1 ms at 6k items (the palette library's own matcher took
// 23–51 ms). Plain data, so server tests can check it (server/test/search.test.ts).

export type EntryKind = "page" | "action" | "item" | "group" | "po" | "run" | "bill" | "bom" | "receipt" | "vendor" | "project" | "user";

export type EntryInput = {
  kind: EntryKind;
  id: string;
  title: string;
  /** Shown muted on the right: a status, a vendor, a SKU. */
  hint?: string;
  /** Where Enter goes (a path), or an action's name for the palette to run. */
  to: string;
  /** Codes that open this record when typed or scanned exactly (any case). */
  codes?: readonly string[];
  /** More words it answers to (a vendor's name, a status). */
  words?: string;
};

/** `codeStarts` and `wordStarts` put a newline or space before every code and title word, so "starts with" is one test. */
export type Entry = EntryInput & { hay: string; codeStarts: string; wordStarts: string; order: string };

export type SearchIndex = { entries: Entry[]; exact: Map<string, Entry[]> };

const norm = (s: string) => s.trim().toLowerCase();
/** Sorts like a person reads codes ("gcu1300" before "gcu1304", "po-999" before "po-1000") with a plain string compare. */
const sortKey = (s: string) => s.replace(/\d+/g, (d) => d.padStart(12, "0"));

export function buildIndex(inputs: readonly EntryInput[]): SearchIndex {
  const entries: Entry[] = [];
  const exact = new Map<string, Entry[]>();
  for (const e of inputs) {
    const lowCodes = (e.codes ?? []).map(norm).filter(Boolean);
    const lowTitle = norm(e.title);
    const entry: Entry = {
      ...e,
      hay: [...lowCodes, lowTitle, norm(e.hint ?? ""), norm(e.words ?? "")].join(" "),
      codeStarts: lowCodes.map((c) => `\n${c}`).join(""),
      wordStarts: ` ${lowTitle}`,
      order: sortKey(lowCodes[0] ?? lowTitle),
    };
    entries.push(entry);
    for (const c of new Set(lowCodes)) {
      const list = exact.get(c);
      if (list) list.push(entry);
      else exact.set(c, [entry]);
    }
  }
  // In code order once, here: then the first matches a search finds of each kind are also the first in order.
  entries.sort((a, b) => (a.order < b.order ? -1 : a.order > b.order ? 1 : 0));
  return { entries, exact };
}

export type Hit = Entry & { rank: number };

const RANKS = 4;

/**
 * What a query finds, best first: rank 0 is an exact code (a scan), 1 a code that starts with it, 2 a title that starts
 * with it (or a title word with its first word), 3 every word found anywhere. At most `perKind` of each kind, in code
 * order within a rank (GCU1300 before GCU1304); one kind never crowds out another.
 */
export function search(ix: SearchIndex, query: string, perKind = 8): Hit[] {
  const q = norm(query);
  if (!q) return [];
  const words = q.split(/\s+/);
  const codeStart = `\n${q}`;
  const wordStart = ` ${words[0]}`;
  const exact = new Set(ix.exact.get(q) ?? []);
  // Per kind, per rank: entries only until the end (a Hit is made for what's shown, never for the long tail).
  const kept = new Map<EntryKind, Entry[][]>();
  const slot = (kind: EntryKind) => {
    let ranks = kept.get(kind);
    if (!ranks) kept.set(kind, (ranks = Array.from({ length: RANKS }, () => [])));
    return ranks;
  };
  for (const e of exact) slot(e.kind)[0].push(e);
  for (const e of ix.entries) {
    if (exact.has(e)) continue;
    let all = true;
    for (const w of words) {
      if (!e.hay.includes(w)) {
        all = false;
        break;
      }
    }
    if (!all) continue;
    const rank = e.codeStarts.includes(codeStart) ? 1 : e.wordStarts.includes(wordStart) ? 2 : 3;
    const list = slot(e.kind)[rank];
    if (list.length < perKind) list.push(e);
  }
  const out: Hit[] = [];
  const taken = new Map<EntryKind, number>();
  for (let rank = 0; rank < RANKS; rank++) {
    for (const [kind, ranks] of kept) {
      for (const e of ranks[rank]) {
        const n = taken.get(kind) ?? 0;
        if (rank > 0 && n >= perKind) break;
        taken.set(kind, n + 1);
        out.push({ ...e, rank });
      }
    }
  }
  return out;
}
